/**
 * Vocabulário do exame oftalmológico no ditado (spec 009, 02/10/2026).
 *
 * Pedido do Leopoldo, com a fala real: "fundoscopia: disco óptico
 * normocorado, escavação papilar fisiológica, mácula e vasos sem alterações;
 * biomicroscopia: córnea transparente, íris trófica, fácico" virou "disco
 * óptico normal, corado … macrovasos sem alterações" e "córnea transparente,
 * lente transparente". O modelo ouve o áudio direto: termo do jargão que ele
 * não espera vira a palavra comum mais parecida ("normal, corado",
 * "macrovasos"), some ("íris trófica") ou vira paráfrase ("fácico" → "lente
 * transparente"). O "macrovasos" já estava no teste de 02/10 de manhã, copiado
 * de um card real — não foi acaso.
 *
 * Duas defesas, nesta ordem:
 *  1. `EXAM_VOCAB_PROMPT` — os termos de cada estrutura à vista do modelo
 *     (Ao vivo e gravação). Com a lista ele reconhece o termo no áudio.
 *  2. `repairFindingText` — conserto determinístico só das trocas JÁ VISTAS,
 *     só nos campos de achado. Não é sinônimo nem reescrita: cada regra troca
 *     uma forma que não existe no jargão pela que o médico disse.
 */

export const EXAM_VOCAB_PROMPT = `### Vocabulário do exame (ouça com ele)
O médico dita o exame no jargão da oftalmologia. Quando o som lembrar um destes termos, é o termo — escreva como aqui:
- Biomicroscopia: conjuntiva calma; hiperemia conjuntival; pterígio; pinguécula; hiposfagma; córnea transparente / clara; ceratite ponteada; leucoma; guttata; câmara anterior ampla / formada / profunda / rasa; Tyndall; flare; hifema; hipópio; íris trófica; rubeose; sinéquias; pupilas isocóricas e fotorreagentes; discoria; fácico; pseudofácico; afácico; cristalino transparente; facoesclerose; catarata nuclear / cortical / subcapsular posterior, em cruzes ("++/4+") ou N1, N2… C1… P1…; LIO tópica / centrada / na bolsa; opacidade de cápsula posterior; pseudoexfoliação; blefarite; meibomite; calázio; triquíase; ptose; dermatocálase.
- Fundoscopia: meios transparentes; vítreo transparente; descolamento do vítreo posterior; anel de Weiss; disco óptico normocorado; disco corado / hipocorado / pálido; bordos / contornos nítidos; rima neural preservada; escavação fisiológica / papilar fisiológica; escavação 0,3 (relação E/D); atrofia peripapilar; mácula livre / sem alterações; brilho / reflexo foveal presente; drusas; alterações do EPR; membrana epirretiniana; edema macular; vasos de calibre e trajeto normais; relação arteriovenosa 2:3; cruzamentos arteriovenosos; retina aplicada; periferia sem roturas; degeneração lattice / em paliçada; microaneurismas; hemorragias; exsudatos duros / algodonosos; neovasos; marcas de laser.
- Mapeamento de retina: sob midríase; depressão escleral; vítreo transparente; descolamento do vítreo posterior; tração vitreorretiniana; retina aplicada em 360°; periferia sem roturas; degeneração lattice / em paliçada; degeneração pavimentosa; branco sem pressão; buraco atrófico; rotura em ferradura; opérculo; retinosquise; descolamento de retina regmatogênico; cicatrizes de fotocoagulação / laser; bloqueio a laser.
Trocas que já aconteceram — não repita:
- "normocorado" é UMA palavra (disco de cor normal): nunca "normal, corado" nem "normo corado".
- "mácula e vasos" são duas estruturas: nunca "macrovasos".
- "fácico", "pseudofácico", "afácico" dizem se o olho tem o cristalino natural, a LIO ou nenhum: escreva o termo dito — nunca "básico", e nunca "lente transparente" ou "cristalino transparente" no lugar dele.
- "íris trófica" não é "atrófica" (outro achado: escreva o que ele disse). "Biomicroscopia", não "videomicroscopia"; "fundoscopia", não "endoscopia".
Cada termo dito entra, na ordem e com as palavras dele: "conjuntiva calma, íris trófica, pseudofácico" são TRÊS achados — os três vão; nenhum some, nenhum é resumido nem trocado por sinônimo.`;

type Reparo = [RegExp, string];

/**
 * Trocas já vistas em card real → o termo dito. Só formas que não existem
 * no jargão ("macrovasos" não se usa na fundoscopia; "básico" não é achado de
 * biomicroscopia), pra o conserto nunca pisar no que o médico disse de fato.
 */
const REPAROS: Record<string, Reparo[]> = {
  fundoscopia: [
    // "normocorado" partido: "normal, corado", "normal e corada", "normo corado", "normo-corado".
    [/\bnorm(?:al|o)(?:\s*,\s*|\s*-\s*|\s+)(?:e\s+)?corad([oa]s?)\b/gi, 'normocorad$1'],
    [/\bmacro[\s-]?vasos\b/gi, 'mácula e vasos'],
  ],
  biomicroscopia: [
    // "fácico" ouvido como "básico"; "pseudo fácico" separado.
    [/\bpseudo[\s-]?(?:f[aá]c|b[aá]s)ic([oa])(?![a-zà-ú])/gi, 'pseudofácic$1'],
    [/\bb[aá]sic([oa])(?![a-zà-ú])/gi, 'fácic$1'],
  ],
};

/** Mantém a maiúscula do começo do trecho trocado ("Normal, corado" → "Normocorado"). */
function comCaixa(original: string, novo: string): string {
  return /^[A-ZÀ-Ý]/.test(original) ? novo.charAt(0).toUpperCase() + novo.slice(1) : novo;
}

/** Conserta, no achado em texto do módulo, as trocas de áudio já vistas. */
export function repairFindingText(moduleId: string, text: string): string {
  let out = text;
  for (const [re, termo] of REPAROS[moduleId] ?? []) {
    out = out.replace(re, (trecho, ...grupos) => comCaixa(trecho, termo.replace('$1', String(grupos[0] ?? ''))));
  }
  return out;
}
